import { useQuery } from "@tanstack/react-query";
import { type FormEvent, useState } from "react";
import { api, download, jsonBody } from "../../api/client";
import type { Contact, ListResponse } from "../../api/types";
import { useSensitiveAction } from "../../auth/SensitiveActionGate";
import { formatDate, titleCase } from "../../lib/format";
import {
  Card,
  CardHeader,
  DataTable,
  EmptyState,
  ErrorPanel,
  Field,
  FormActions,
  Notice,
  ReasonDialog,
  StatusPill,
} from "../../ui/components";
import { PanelSkeleton } from "../../ui/skeletons";

interface PrivacyRequest {
  id: string;
  tenantId: string;
  customerId: string;
  customerName?: string;
  type: string;
  jurisdiction: string;
  status: string;
  source: string;
  requesterReference: string;
  scope: Record<string, unknown>;
  systemsSearched: string[];
  exemptions: Record<string, unknown>;
  dueAt: string;
  decisionReason?: string;
  assignedTo?: string;
  identityVerifiedAt?: string;
  resolutionObjectId?: string;
  fulfilmentStartedAt?: string;
  notifiedAt?: string;
  version: number;
  createdAt: string;
  updatedAt: string;
  completedAt?: string;
}
interface LegalHold {
  id: string;
  tenantId: string;
  scope: Record<string, unknown>;
  reason: string;
  approvedBy: string;
  startsAt: string;
  expiresAt: string;
  releasedAt?: string;
  releasedBy?: string;
  releaseReason?: string;
  version: number;
  createdAt: string;
  updatedAt: string;
}

const categories = [
  "profile",
  "contacts",
  "bookings",
  "documents",
  "communications",
  "finance",
  "portal",
  "audit",
];
const systems = [
  "profile",
  "bookings",
  "documents",
  "communications",
  "finance",
  "portal",
  "audit",
  "object_storage",
  "integrations",
  "backups",
];

export function PrivacySettings() {
  const sensitive = useSensitiveAction({
    setupTitle: "Protect privacy changes with two-step verification",
    lockedTitle: "Confirm your identity to manage private information",
    lockedDescription:
      "Privacy requests and legal holds can reveal or change personal information. Enter a fresh code before recording a decision.",
  });
  const requests = useQuery({
    queryKey: ["privacy", "requests"],
    queryFn: () => api<ListResponse<PrivacyRequest>>("/api/v1/privacy/requests"),
  });
  const holds = useQuery({
    queryKey: ["privacy", "legal-holds"],
    queryFn: () => api<ListResponse<LegalHold>>("/api/v1/privacy/legal-holds"),
  });
  const customers = useQuery({
    queryKey: ["contacts"],
    queryFn: () => api<ListResponse<Contact>>("/api/v1/contacts"),
  });
  const [selected, setSelected] = useState<PrivacyRequest | null>(null);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState<unknown>();
  const [releasingHold, setReleasingHold] = useState<LegalHold>();

  async function createRequest(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const formElement = event.currentTarget;
    const form = new FormData(formElement);
    setWorking(true);
    setError(undefined);
    try {
      const item = await api<PrivacyRequest>("/api/v1/privacy/requests", {
        method: "POST",
        body: jsonBody({
          customerId: String(form.get("customerId")),
          type: String(form.get("type")),
          jurisdiction: String(form.get("jurisdiction")),
          requesterReference: String(form.get("requesterReference")),
          categories: form.getAll("categories").map(String),
        }),
      });
      formElement.reset();
      await requests.refetch();
      setSelected(item);
    } catch (caught) {
      if (!sensitive.markProtectedError(caught)) setError(caught);
    } finally {
      setWorking(false);
    }
  }

  async function verifyIdentity(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!selected) return;
    const form = new FormData(event.currentTarget);
    setWorking(true);
    setError(undefined);
    try {
      const item = await api<PrivacyRequest>(
        `/api/v1/privacy/requests/${selected.id}/identity-verification`,
        {
          method: "POST",
          body: jsonBody({
            method: String(form.get("method")),
            reason: String(form.get("reason")),
            version: selected.version,
          }),
        },
      );
      setSelected(item);
      await requests.refetch();
    } catch (caught) {
      if (!sensitive.markProtectedError(caught)) setError(caught);
    } finally {
      setWorking(false);
    }
  }

  async function changeState(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!selected) return;
    const form = new FormData(event.currentTarget);
    setWorking(true);
    setError(undefined);
    try {
      const item = await api<PrivacyRequest>(`/api/v1/privacy/requests/${selected.id}/state`, {
        method: "PUT",
        body: jsonBody({
          status: String(form.get("status")),
          reason: String(form.get("reason")),
          systemsSearched: form.getAll("systems").map(String),
          exemptions: {},
          version: selected.version,
        }),
      });
      setSelected(item);
      await requests.refetch();
    } catch (caught) {
      if (!sensitive.markProtectedError(caught)) setError(caught);
    } finally {
      setWorking(false);
    }
  }

  async function startFulfilment() {
    if (!selected) return;
    setWorking(true);
    setError(undefined);
    try {
      const item = await api<PrivacyRequest>(`/api/v1/privacy/requests/${selected.id}/fulfilment`, {
        method: "POST",
        body: jsonBody({ version: selected.version }),
      });
      setSelected(item);
      await requests.refetch();
    } catch (caught) {
      if (!sensitive.markProtectedError(caught)) setError(caught);
    } finally {
      setWorking(false);
    }
  }

  async function downloadExport() {
    if (!selected?.resolutionObjectId) return;
    setWorking(true);
    setError(undefined);
    try {
      await download(`/api/v1/privacy/requests/${selected.id}/export`);
      // The first download stamps the request as notified, so the list is refreshed to show it.
      await requests.refetch();
    } catch (caught) {
      if (!sensitive.markProtectedError(caught)) setError(caught);
    } finally {
      setWorking(false);
    }
  }

  async function completeManual(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!selected) return;
    const form = new FormData(event.currentTarget);
    setWorking(true);
    setError(undefined);
    try {
      const item = await api<PrivacyRequest>(`/api/v1/privacy/requests/${selected.id}/complete`, {
        method: "POST",
        body: jsonBody({
          reason: String(form.get("reason")),
          systemsSearched: form.getAll("systems").map(String),
          exemptions: {},
          version: selected.version,
        }),
      });
      setSelected(item);
      await requests.refetch();
    } catch (caught) {
      if (!sensitive.markProtectedError(caught)) setError(caught);
    } finally {
      setWorking(false);
    }
  }

  async function createHold(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const formElement = event.currentTarget;
    const form = new FormData(formElement);
    const allPersonalData = form.get("allPersonalData") === "on";
    setWorking(true);
    setError(undefined);
    try {
      await api("/api/v1/privacy/legal-holds", {
        method: "POST",
        body: jsonBody({
          customerId: allPersonalData ? "" : String(form.get("customerId")),
          allPersonalData,
          reason: String(form.get("reason")),
          expiresAt: new Date(String(form.get("expiresAt"))).toISOString(),
        }),
      });
      formElement.reset();
      await holds.refetch();
    } catch (caught) {
      if (!sensitive.markProtectedError(caught)) setError(caught);
    } finally {
      setWorking(false);
    }
  }

  async function releaseHold(reason: string) {
    if (!releasingHold) return;
    setWorking(true);
    setError(undefined);
    try {
      await api<void>(`/api/v1/privacy/legal-holds/${releasingHold.id}`, {
        method: "DELETE",
        body: jsonBody({ reason, version: releasingHold.version }),
      });
      setReleasingHold(undefined);
      await holds.refetch();
    } catch (caught) {
      if (!sensitive.markProtectedError(caught)) setError(caught);
    } finally {
      setWorking(false);
    }
  }

  if (requests.isLoading || holds.isLoading || customers.isLoading) return <PanelSkeleton />;
  if (requests.error || holds.error || customers.error)
    return <ErrorPanel error={requests.error ?? holds.error ?? customers.error} />;
  const requestItems = requests.data?.items ?? [];
  const holdItems = holds.data?.items ?? [];
  const canAutomate =
    selected?.status === "in_progress" &&
    Boolean(selected.identityVerifiedAt) &&
    ["access", "portability", "erasure"].includes(selected.type) &&
    !selected.fulfilmentStartedAt;
  const canComplete =
    selected?.status === "in_progress" &&
    Boolean(selected.identityVerifiedAt) &&
    ["correction", "restriction", "objection"].includes(selected.type);

  return (
    <div className="stack">
      <Notice tone="warning" title="Controlled compliance workflow">
        Every privacy mutation and legal-hold change requires recent MFA verification, tenant
        permission, a reason, version checks, and an audit event. This tool supports the process;
        legal advice and identity-verification judgment remain the customer’s responsibility.
      </Notice>
      {sensitive.view}
      {error ? <ErrorPanel error={error} /> : null}
      <Card>
        <CardHeader
          title="Record a data-rights request"
          description="Deadlines are calculated from the selected jurisdiction. Identity verification is always the first operational gate."
        />
        <form className="form-grid" onSubmit={createRequest}>
          <Field label="Customer">
            <select name="customerId" required defaultValue="">
              <option value="" disabled>
                Select customer
              </option>
              {customers.data?.items.map((customer) => (
                <option key={customer.id} value={customer.id}>
                  {customer.organisation
                    ? `${customer.displayName} (${customer.organisation})`
                    : customer.displayName}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Request type">
            <select name="type" defaultValue="access">
              <option value="access">Access</option>
              <option value="correction">Correction</option>
              <option value="erasure">Erasure</option>
              <option value="restriction">Restriction</option>
              <option value="portability">Portability</option>
              <option value="objection">Objection</option>
            </select>
          </Field>
          <Field label="Jurisdiction">
            <select name="jurisdiction" defaultValue="US-GA">
              <option value="US-GA">United States: Georgia</option>
              <option value="UK">United Kingdom</option>
              <option value="EU">European Union</option>
            </select>
          </Field>
          <Field label="Requester reference">
            <input
              name="requesterReference"
              required
              minLength={3}
              placeholder="Email subject, case number, or call reference"
            />
          </Field>
          <fieldset className="full">
            <legend>Data categories</legend>
            <div className="form-grid">
              {categories.map((category, index) => (
                <label className="check-field" key={category}>
                  <input
                    name="categories"
                    value={category}
                    type="checkbox"
                    defaultChecked={index < 3}
                  />{" "}
                  {titleCase(category)}
                </label>
              ))}
            </div>
          </fieldset>
          <div className="full">
            <FormActions
              submitting={working}
              disabled={!sensitive.unlocked}
              submitLabel="Record request"
            />
          </div>
        </form>
      </Card>
      <div className="content-grid">
        <Card className="span-7">
          <CardHeader title="Requests" description="Open and completed privacy cases." />
          {requestItems.length ? (
            <DataTable
              caption="Privacy requests"
              items={requestItems}
              getKey={(item) => item.id}
              columns={[
                {
                  label: "Customer",
                  render: (item) => (
                    <button
                      className="button ghost small"
                      type="button"
                      onClick={() => setSelected(item)}
                    >
                      {item.customerName || item.customerId}
                    </button>
                  ),
                },
                { label: "Type", render: (item) => titleCase(item.type) },
                { label: "Jurisdiction", render: (item) => item.jurisdiction },
                { label: "Due", render: (item) => formatDate(item.dueAt) },
                { label: "Status", render: (item) => <StatusPill value={item.status} /> },
              ]}
            />
          ) : (
            <EmptyState
              title="No privacy requests"
              description="New data-rights cases will appear here."
            />
          )}
        </Card>
        <Card className="span-5">
          <CardHeader
            title="Selected request"
            description="Choose a request to work through its gated actions."
          />
          {selected ? (
            <div className="stack">
              <div className="split">
                <div>
                  <strong>{selected.customerName}</strong>
                  <br />
                  <small>{selected.requesterReference}</small>
                </div>
                <StatusPill value={selected.status} />
              </div>
              <dl className="definition-list">
                <dt>Type</dt>
                <dd>{titleCase(selected.type)}</dd>
                <dt>Due</dt>
                <dd>{formatDate(selected.dueAt)}</dd>
                <dt>Identity verified</dt>
                <dd>
                  {selected.identityVerifiedAt ? formatDate(selected.identityVerifiedAt) : "No"}
                </dd>
                <dt>Systems searched</dt>
                <dd>{selected.systemsSearched.join(", ") || "Not recorded"}</dd>
                <dt>Export archive</dt>
                <dd>
                  {selected.resolutionObjectId ? (
                    <button
                      className="button secondary small"
                      type="button"
                      disabled={working || !sensitive.unlocked}
                      onClick={downloadExport}
                    >
                      Download export
                    </button>
                  ) : (
                    "Not generated"
                  )}
                </dd>
                <dt>Requester notified</dt>
                <dd>{selected.notifiedAt ? formatDate(selected.notifiedAt) : "Not yet"}</dd>
                <dt>Version</dt>
                <dd>{selected.version}</dd>
              </dl>
              {selected.status === "identity_verification" ? (
                <form className="form-stack" onSubmit={verifyIdentity}>
                  <Field label="Verification method">
                    <select name="method" defaultValue="known_contact_callback">
                      <option value="portal_session">Authenticated portal session</option>
                      <option value="known_contact_callback">Known contact callback</option>
                      <option value="in_person">In person</option>
                      <option value="document_review">Document review</option>
                      <option value="other">Other</option>
                    </select>
                  </Field>
                  <Field label="Verification record">
                    <textarea name="reason" required minLength={10} />
                  </Field>
                  <FormActions
                    submitting={working}
                    disabled={!sensitive.unlocked}
                    submitLabel="Verify identity"
                  />
                </form>
              ) : null}
              {canAutomate ? (
                <button
                  className="button primary"
                  type="button"
                  disabled={working || !sensitive.unlocked}
                  onClick={startFulfilment}
                >
                  Start protected fulfillment job
                </button>
              ) : null}
              {canComplete ? (
                <form className="form-stack" onSubmit={completeManual}>
                  <Field label="Completion reason">
                    <textarea name="reason" required minLength={10} />
                  </Field>
                  <fieldset>
                    <legend>Systems searched</legend>
                    <div className="form-grid">
                      {systems.map((system) => (
                        <label className="check-field" key={`complete-${system}`}>
                          <input
                            name="systems"
                            value={system}
                            type="checkbox"
                            defaultChecked={["profile", "bookings", "finance", "portal"].includes(
                              system,
                            )}
                          />{" "}
                          {titleCase(system)}
                        </label>
                      ))}
                    </div>
                  </fieldset>
                  <FormActions
                    submitting={working}
                    disabled={!sensitive.unlocked}
                    submitLabel="Complete request"
                  />
                </form>
              ) : null}
              {!["fulfilled", "denied", "withdrawn"].includes(selected.status) ? (
                <form className="form-stack" onSubmit={changeState}>
                  <Field label="Close without fulfillment">
                    <select name="status" defaultValue="withdrawn">
                      <option value="withdrawn">Withdrawn by requester</option>
                      <option value="denied">Denied with reason</option>
                    </select>
                  </Field>
                  <Field label="Decision reason">
                    <textarea name="reason" required minLength={10} />
                  </Field>
                  <fieldset>
                    <legend>Systems searched, if applicable</legend>
                    <div className="form-grid">
                      {systems.map((system) => (
                        <label className="check-field" key={`state-${system}`}>
                          <input name="systems" value={system} type="checkbox" />{" "}
                          {titleCase(system)}
                        </label>
                      ))}
                    </div>
                  </fieldset>
                  <FormActions
                    submitting={working}
                    disabled={!sensitive.unlocked}
                    submitLabel="Record decision"
                  />
                </form>
              ) : null}
            </div>
          ) : (
            <EmptyState
              title="No request selected"
              description="Select a customer in the request table."
            />
          )}
        </Card>
      </div>
      <Card>
        <CardHeader
          title="Legal holds"
          description="Preserve personal data where an approved legal or regulatory obligation overrides normal deletion."
        />
        <form className="form-grid" onSubmit={createHold}>
          <Field label="Customer">
            <select name="customerId" defaultValue="">
              <option value="">Select customer for a scoped hold</option>
              {customers.data?.items.map((customer) => (
                <option key={customer.id} value={customer.id}>
                  {customer.organisation
                    ? `${customer.displayName} (${customer.organisation})`
                    : customer.displayName}
                </option>
              ))}
            </select>
          </Field>
          <label className="check-field">
            <input name="allPersonalData" type="checkbox" /> Hold all personal data for this tenant
          </label>
          <Field label="Expires at">
            <input name="expiresAt" type="datetime-local" required />
          </Field>
          <Field label="Approved reason">
            <textarea name="reason" required minLength={10} />
          </Field>
          <div className="full">
            <FormActions
              submitting={working}
              disabled={!sensitive.unlocked}
              submitLabel="Create legal hold"
            />
          </div>
        </form>
        {holdItems.length ? (
          <DataTable
            caption="Legal holds"
            items={holdItems}
            getKey={(hold) => hold.id}
            columns={[
              {
                label: "Scope",
                render: (hold) =>
                  hold.scope.allPersonalData
                    ? "All personal data"
                    : String(hold.scope.customerId || "Customer"),
              },
              { label: "Reason", render: (hold) => hold.reason },
              { label: "Expires", render: (hold) => formatDate(hold.expiresAt) },
              {
                label: "Status",
                render: (hold) => <StatusPill value={hold.releasedAt ? "released" : "active"} />,
              },
              {
                label: "Action",
                render: (hold) =>
                  hold.releasedAt ? (
                    hold.releaseReason || "Released"
                  ) : (
                    <button
                      className="button danger small"
                      type="button"
                      disabled={working || !sensitive.unlocked}
                      onClick={() => setReleasingHold(hold)}
                    >
                      Release
                    </button>
                  ),
              },
            ]}
          />
        ) : (
          <EmptyState
            title="No legal holds"
            description="Only create a hold when an authorized retention obligation exists."
          />
        )}
      </Card>
      <ReasonDialog
        open={Boolean(releasingHold)}
        onClose={() => setReleasingHold(undefined)}
        onSubmit={releaseHold}
        title="Release legal hold"
        description="This resumes normal retention and deletion eligibility for data that is not covered by another hold."
        initialReason="The approved retention requirement has ended."
        submitLabel="Release hold"
        tone="danger"
        minLength={10}
        submitting={working}
        disabled={!sensitive.unlocked}
      />
    </div>
  );
}
